// Транспорт до `/api/sync/powersync/write` — лист графа импортов.
//
// Один и тот же эндпоинт зовут двое: коннектор (штатная выгрузка CRUD-очереди)
// и центр восстановления (повтор отказавшей операции). Раньше запрос умел
// собирать только коннектор, поэтому «Повторить» шло окольным путём через
// локальную SQLite и не могло узнать вердикт сервера. Общий транспорт даёт
// обоим одинаковые заголовки, разбор ответа и типы результатов.
import { getAccessToken } from "@/hooks/use-session";

// Web: same-origin "" → запросы идут через Vite proxy /api → :3000
//      (cookie работает, нет CORS). Capacitor: абсолютный VITE_API_URL.
// VITE_SERVER_URL — legacy override (если задан явно).
const SERVER_URL = (import.meta.env?.VITE_API_URL ??
	import.meta.env?.VITE_SERVER_URL ??
	"") as string;

/** Серверный потолок операций в одном POST (`writeBatchInputSchema`). */
export const MAX_OPS_PER_REQUEST = 100;

export type WriteOpStatus = "success" | "conflict" | "forbidden" | "invalid";

export interface WriteOperationOut {
	op: "PUT" | "PATCH" | "DELETE";
	table: string;
	id: string;
	data?: Record<string, unknown>;
	idempotencyKey: string;
	clientTimestamp: number;
}

export interface WriteOpResult {
	status: WriteOpStatus;
	id: string;
}

/** 401 от эндпоинта — креды протухли, вызывающий обязан их сбросить. */
export class PowerSyncWriteUnauthorized extends Error {
	constructor() {
		super("powersync.write: UNAUTHORIZED — credentials refresh required");
		this.name = "PowerSyncWriteUnauthorized";
	}
}

/**
 * Отправляет группу операций одним POST и возвращает по-операционные вердикты.
 *
 * Бросает при транспортных / протокольных ошибках (вся группа ретраится
 * целиком) и при 401. Per-op отказы (`invalid` / `forbidden` / `conflict`)
 * приезжают в результатах при HTTP 200 — это НЕ исключение.
 */
export async function postWriteOperations(
	operations: readonly WriteOperationOut[],
	checkpoint: string,
): Promise<WriteOpResult[]> {
	const headers: Record<string, string> = { "content-type": "application/json" };
	const accessToken = await getAccessToken();
	if (accessToken) headers.authorization = `Bearer ${accessToken}`;

	const res = await fetch(`${SERVER_URL}/api/sync/powersync/write`, {
		method: "POST",
		credentials: "include",
		headers,
		body: JSON.stringify({ batch: operations, checkpoint }),
	});

	if (res.status === 401) throw new PowerSyncWriteUnauthorized();
	if (!res.ok) {
		throw new Error(`powersync.write: HTTP ${res.status} (group=${checkpoint})`);
	}

	let body: { results?: WriteOpResult[] } = {};
	try {
		body = (await res.json()) as { results?: WriteOpResult[] };
	} catch {
		throw new Error(`powersync.write: invalid JSON body (group=${checkpoint})`);
	}
	const results = body.results ?? [];
	if (results.length !== operations.length) {
		throw new Error(
			`powersync.write: results length ${results.length} != ops ${operations.length} (group=${checkpoint})`,
		);
	}
	return results;
}
